import events from './event.js';
import nav from './navigate.js';
import { buttonString, textString, getColor } from './texts.js';
import Logger from './logger.js';
import Storage from './storage.js';
import HelpPopup from './HelpPopup.js';
import _ from './translate.js';

/**
 * CropImageToolbar class to manage the toolbar for cropping images.
 * It adds a button to change the image and triggers an upload function when clicked.
 */
class CropImageToolbar {
    toolbar = null;
    tooltip = null;
    options = {};
    helpPopup = null;

    /**
     * @param {HTMLElement} toolbar - The toolbar element to which the button will be added.
     */
    constructor(toolbar, options = {}) {
        this.toolbar = toolbar;
        this.options = options;

        this.addEventListeners();

        this.addClosePopupButton();
        this.addChangeImageButton();
        this.addPriceButton();
        this.addSizeButton();
        // this.addResolutionButton();

        this.addMenuButton();

        this.addAdjustButton();

        this.helpPopup = new HelpPopup({
            parentElement: this.toolbar,
            textKey: 'adjust_mode_no_size_selected',
            defaultText: _('Cannot enter adjust mode without a selected size.'),
        });

    }

    addEventListeners() {
        // Stäng menyn vid klick på bakgrunden (inte på knapparna)
        this.toolbar.addEventListener('click', (e) => {
            if (!this.toolbar.classList.contains('mscp-open')) return;
            if (e.target.closest('.mscp-menu-item, .mscp-floating-button')) return;
            events.trigger('closeMainMenu');
        });

        events.add('stepChanged', () => {
            events.trigger('closeMainMenu');
        });
        events.add('openMainMenu', () => {
            this.toolbar.classList.add('mscp-open');
        });
        events.add('closeMainMenu', () => {
            this.toolbar.classList.remove('mscp-open');
        });
        events.add('toggleMainMenu', () => {
            this.toolbar.classList.toggle('mscp-open');
        });
        events.add('imageLoaded', async () => {
            this.toolbar.classList.remove('mscp-open');
        });
    }

    addClosePopupButton() {
        const closeButtonDiv = document.createElement('div');
        closeButtonDiv.classList.add('mscp-close-button', 'mscp-menu-item');
        this.toolbar.appendChild(closeButtonDiv);
        closeButtonDiv.addEventListener('click', () => {
            events.trigger('hidePopup');
        });

        const closeText = document.createElement('span');
        closeText.classList.add('mscp-close-text', 'mscp-menu-label');
        closeText.innerHTML = buttonString('menu_close_popup_label', 'Leave popup');
        closeButtonDiv.appendChild(closeText);

        const closeIcon = document.createElement('span');
        closeIcon.classList.add('mscp-close-icon', 'mscp-menu-icon', 'mscp-floating-button');
        closeIcon.innerHTML = 'leave';
        closeButtonDiv.appendChild(closeIcon);
    }

    addChangeImageButton() {
        const changeImageButton = document.createElement('div');
        changeImageButton.classList.add('mscp-change-image-button', 'mscp-menu-item');
        this.toolbar.appendChild(changeImageButton);
        changeImageButton.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent the click event from bubbling up
            e.preventDefault(); // Prevent default action if any
            events.trigger('showFileUpload', true); // Trigger the upload function to show the dropzone
            if (this.options.changeImageCallback) {
                this.options.changeImageCallback(); // Call the change image callback if provided
            }
        });

        const changeImageButtonText = document.createElement('span');
        changeImageButtonText.classList.add('mscp-change-image-text', 'mscp-menu-label');
        changeImageButtonText.innerHTML = textString('menu_change_image_label', 'Change Image');
        changeImageButton.appendChild(changeImageButtonText);

        const changeImageButtonIcon = document.createElement('span');
        changeImageButtonIcon.classList.add('mscp-change-image-icon', 'mscp-menu-icon', 'mscp-floating-button');
        changeImageButtonIcon.innerHTML = 'change image';
        changeImageButton.appendChild(changeImageButtonIcon);
    }


    addPriceButton() {

        const priceButtonLabel = buttonString('menu_price_label'); // leave empty to hide
        if (priceButtonLabel && priceButtonLabel.trim() !== '') {

            const priceButtonDiv = document.createElement('div');
            priceButtonDiv.classList.add('mscp-price-button', 'mscp-menu-item');
            this.toolbar.appendChild(priceButtonDiv);
            priceButtonDiv.addEventListener('click', () => {
                const activeStep = nav.getActiveStep();
                if (activeStep == 'paper') {
                    events.trigger('openAdditionalInfoPopup', {
                        text_prop_name: 'price_general_text'
                    });
                }
                else {
                    const presentation = Storage.get('presentation');
                    events.trigger('openAdditionalInfoPopup', {
                        text_prop_name: `price_${presentation}_text`
                    });
                }
            });

            const priceText = document.createElement('span');
            priceText.classList.add('mscp-price-label', 'mscp-menu-label');
            priceText.innerHTML = priceButtonLabel;
            priceButtonDiv.appendChild(priceText);

            const priceIcon = document.createElement('span');
            priceIcon.classList.add('mscp-price-icon', 'mscp-menu-icon', 'mscp-floating-button');
            priceIcon.innerHTML = 'price';
            priceButtonDiv.appendChild(priceIcon);
        } // end if priceButtonLabel

    }

    addSizeButton() {
        const buttonLabel = textString('menu_size_label', 'Information');
        const sizeButtonDiv = document.createElement('div');
        sizeButtonDiv.classList.add('mscp-size-button', 'mscp-menu-item');
        this.toolbar.appendChild(sizeButtonDiv);
        sizeButtonDiv.addEventListener('click', () => {
            events.trigger('openAdditionalInfoPopup', {
                text_prop_name: nav.getActiveStep() + '_info_text'
            });
        });

        const sizeLabel = document.createElement('span');
        sizeLabel.classList.add('mscp-size-label', 'mscp-menu-label');
        sizeLabel.innerHTML = buttonLabel;

        // sizeLabel.innerHTML = textString(nav.getActiveStep() + '_navigation_label', _('Navigation'));
        sizeButtonDiv.appendChild(sizeLabel);
        // Update the label when the step changes
        // events.add('stepChanged', () => {
        //     infoLabel.innerHTML = buttonString(nav.getActiveStep() + '_navigation_label', _('Navigation'));
        // });

        const sizeIcon = document.createElement('span');
        sizeIcon.classList.add('mscp-size-icon', 'mscp-menu-icon', 'mscp-floating-button');
        sizeIcon.innerHTML = 'size';
        sizeButtonDiv.appendChild(sizeIcon);

    }

    addAdjustButton() {

        // add info bar
        // const adjustInfobar = document.createElement('div');
        // adjustInfobar.classList.add('mscp-adjust-infobar');
        // adjustInfobar.innerHTML = textString('adjust_infobar_text');
        // this.toolbar.appendChild(adjustInfobar);

        // add adjust button
        const adjustButton = document.createElement('div');
        adjustButton.classList.add('mscp-adjust-button', 'mscp-button', 'mscp-centered-button');
        this.toolbar.appendChild(adjustButton);
        adjustButton.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent the click event from bubbling up
            // check if size is selected
            const size = Storage.get('size');
            if (!size) {
                this.helpPopup?.toggle(adjustButton);
                return;
            }
            else {
                this.helpPopup?.hide();
            }
            events.trigger('toggleAdjustMode');
        });

        const adjustButtonText = document.createElement('span');
        adjustButtonText.classList.add('mscp-adjust-button-text', 'mscp-button-label');
        adjustButtonText.innerHTML = buttonString('adjust_button', _('Adjust'));
        const adjustButtonColor = getColor('adjust_button_color', null);
        if (adjustButtonColor) {
            // adjustButtonText.style.backgroundColor = adjustButtonColor;
            // create var(--mscp-adjust-button-color) on adjustButton
            adjustButton.style.setProperty('--mscp-adjust-button-color', adjustButtonColor);
        }
        const adjustButtonDoneText = document.createElement('span');
        adjustButtonDoneText.classList.add('mscp-adjust-button-done-text', 'mscp-button-label');
        adjustButtonDoneText.innerHTML = buttonString('adjust_button_done', _('Adjust Done'));
        const adjustButtonDoneColor = getColor('adjust_button_done_color', null);
        if (adjustButtonDoneColor) {
            // adjustButtonDoneText.style.backgroundColor = adjustButtonDoneColor;
            // create var(--mscp-adjust-button-done-color) on adjustButton
            adjustButton.style.setProperty('--mscp-adjust-button-done-color', adjustButtonDoneColor);
        }

        // const adjustButtonIcon = document.createElement('span');
        // adjustButtonIcon.classList.add('mscp-adjust-button-icon', 'icon-wrapper');
        // adjustButtonIcon.innerHTML = mindstore_object?.icon?.adjust_arrow || '✣';

        // adjustButton.appendChild(adjustButtonIcon);
        adjustButton.appendChild(adjustButtonText);
        adjustButton.appendChild(adjustButtonDoneText);



    }



    updateInfoButtonLabel() {
        const activeStep = nav.getActiveStep();
        if (!activeStep) {
            Logger.debug('No active step found, cannot update info button.');
            return;
        }
        this.navigationLabel.innerHTML = textString(activeStep + '_navigation_label', _('Navigation'));
    }

    addMenuButton() {
        const menuButtonDiv = document.createElement('div');
        menuButtonDiv.classList.add('mscp-menu-button', 'mscp-menu-item');
        this.toolbar.appendChild(menuButtonDiv);
        menuButtonDiv.addEventListener('click', () => {
            events.trigger('toggleMainMenu');
        });

        const closeMenuLabel = document.createElement('span');
        closeMenuLabel.classList.add('mscp-close-menu-label', 'mscp-menu-label');
        closeMenuLabel.innerHTML = buttonString('menu_close_menu_label', 'Close Menu');
        menuButtonDiv.appendChild(closeMenuLabel);

        const menuIcon = document.createElement('span');
        menuIcon.classList.add('mscp-menu-icon', 'mscp-the-menu-icon', 'mscp-floating-button');
        menuIcon.innerHTML = 'menu';
        menuButtonDiv.appendChild(menuIcon);
    }
}

export default CropImageToolbar;